import React from "react";
import { CircularProgress } from "@mui/material";

/**
 * ButtonBase — shared logic for all button variants.
 *
 * Not used directly. Each variant wraps this and passes its own
 * `styles` (CSS Module) and `spinnerColor`.
 *
 * Props:
 *  children      ReactNode   — label content (preferred)
 *  text          string      — string fallback when children is not passed
 *  onClick       function
 *  disabled      boolean     (default: false)
 *  isLoading     boolean     (default: false) — shows spinner, hides label + icons
 *  fullWidth     boolean     (default: false) — stretches to 100% width
 *  iconLayout    "inline" | "split"
 *                            "inline" — icon + text grouped, centered as a unit
 *                            "split"  — icon pinned to far right, label fills space
 *                            defaults to "split" when fullWidth=true, "inline" otherwise
 *  startIcon     ReactNode   — rendered before label
 *  endIcon       ReactNode   — rendered after label
 *  type          string      (default: "button")  button | submit | reset
 *  className     string      — extra class for one-off overrides
 *
 * Optional:
 *  labelClassName string     — extra class applied to the label <span> (use for text color overrides)
 *  divider        boolean    (default: false) — renders a vertical rule; use with rightContent
 *  rightContent   ReactNode  — content rendered to the right of the divider (SS2 compound layout)
 *
 * Internal (supplied by each variant wrapper, not by consumers):
 *  styles        object      — CSS Module from the variant's index.module.css
 *  spinnerColor  string      — color of CircularProgress, matches variant text color
 */
const ButtonBase = React.forwardRef(function ButtonBase(
  {
    children,
    text,
    onClick,
    disabled = false,
    isLoading = false,
    fullWidth = false,
    iconLayout,
    startIcon,
    endIcon,
    type = "button",
    className,
    labelClassName,
    divider = false,
    rightContent,
    // internal — passed by variant wrappers
    styles,
    spinnerColor,
  },
  ref,
) {
  const resolvedLayout = iconLayout ?? (fullWidth ? "split" : "inline");

  const classNames = [
    styles.btn,
    fullWidth ? styles.fullWidth : null,
    resolvedLayout === "split" ? styles.layoutSplit : styles.layoutInline,
    isLoading ? styles.loading : null,
    className ?? null,
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <button
      ref={ref}
      type={type}
      disabled={disabled || isLoading}
      onClick={onClick}
      className={classNames}
    >
      {isLoading ? (
        <CircularProgress size={20} sx={{ color: spinnerColor }} />
      ) : divider ? (
        /* ── Compound divider layout (e.g. "⭐ 7.2 | Retake test") ── */
        <>
          <span
            className={[styles.iconWrapper, labelClassName]
              .filter(Boolean)
              .join(" ")}
          >
            {children ?? text}
          </span>
          {rightContent && (
            <>
              <span className={styles.dividerLine} />
              <span className={styles.rightContent}>{rightContent}</span>
            </>
          )}
        </>
      ) : (
        <>
          {startIcon && (
            <span className={styles.iconWrapper} style={{ marginRight: 4 }}>
              {startIcon}
            </span>
          )}

          <span
            className={
              [
                resolvedLayout === "split" ? styles.label : null,
                labelClassName ?? null,
              ]
                .filter(Boolean)
                .join(" ") || undefined
            }
          >
            {children ?? text}
          </span>

          {endIcon && (
            <span className={styles.iconWrapper} style={{ marginLeft: 4 }}>
              {endIcon}
            </span>
          )}
        </>
      )}
    </button>
  );
});

export default ButtonBase;
